// One list tab on the inspection record (H98) — PATTERNS.md BP-80:
// AeronetDataGridPremium inside DetailPremiumBox, the grid owns its rows via
// `data`. Shared by Config List, Aircraft List, BOM, Linked To, ATA History,
// Task/Check List and Special Tasks, which differ only in route and columns.
//
// Read-only unless the tab passes a writer: legacy's add / edit / unlink
// controls on most of these tabs write through routes v6 does not have yet
// (parts-link, BOM, special-task and task-list writers). A + or ACTIONS with
// nothing behind it is the decorative-affordance failure mode, so (+) renders
// only with `onCreate`, ACTIONS and the checkbox column only with `actions`.
// A tab that writes bumps `refreshToken` afterwards so the grid re-reads (BP-80).
//
// Each route returns EVERY row for the record (not paginated), so `data` pages
// the full response and `total = rows.length` is honest (BP-80, "a route that
// is not paginated"). A failed read says so; it is never "No rows", which
// would be a claim about the inspection.
import React, { useCallback, useState } from "react";
import type { GridColDef, GridValidRowModel } from "@mui/x-data-grid-premium";
import { useSnackbar } from "notistack";

import { DetailPremiumBox } from "../../components/DatagridProContnet";
import { sortRows, type ValueOf } from "../../components/datagrid/clientGridParams";
import type { ActionMenuEntry } from "../../components/datagrid/ActionMenu";
import { AeronetDataGridPremium } from "../../components/datagrid/DataGridPremium";
import { useCurrentUser } from "../../hooks/useCurrentUser";

interface Props<T extends GridValidRowModel> {
  /** Grid id — unique and stable (BP-80 rule "name"). */
  name: string;
  /** Human noun for the snackbar and the failed-read label, e.g. "BOM". */
  what: string;
  load: () => Promise<T[]>;
  columns: GridColDef<T>[];
  noRowsLabel: string;
  testId?: string;
  getRowClassName?: (params: { row: T }) => string;
  sx?: object;
  /** The toolbar's (+). Omitted = no (+). */
  onCreate?: () => void;
  /** The toolbar's ACTIONS (selection-aware). Omitted = no ACTIONS, no checkboxes. */
  actions?: ActionMenuEntry[];
  /** Bump after a write: the grid re-reads. */
  refreshToken?: number;
  /**
   * What each column SORTS on, for a tab whose sort must order the whole
   * record and not just the page on screen. The Premium grid sorts only the
   * rows it holds (no `sortingMode` there = MUI's client sort over one page),
   * so a header click on page 1 of 3 ordered 15 rows of 40. Given this, `data`
   * applies the grid's `sort` param to every row before paging (the shared
   * `sortRows`: blanks last either way) — the sort never leaves the browser.
   * Omitted = the tab sorts as before (page-local). Aircraft List passes it
   * (TICKET-22772, "closest across the fleet").
   */
  sortValueOf?: ValueOf<T>;
}

/**
 * `sortValueOf` from a tab's own columns: what each column SHOWS (its
 * valueGetter, else the row's field) is what it sorts on.
 */
export function columnSortValueOf<T extends GridValidRowModel>(columns: GridColDef<T>[]): ValueOf<T> {
  return (row, field) => {
    const col = columns.find((c) => c.field === field);
    return col?.valueGetter
      ? (col.valueGetter as any)(undefined, row, col, undefined)
      : (row as unknown as Record<string, unknown>)[field];
  };
}

export default function RecordTabGrid<T extends GridValidRowModel>({
  name,
  what,
  load,
  columns,
  noRowsLabel,
  testId,
  getRowClassName,
  sx,
  onCreate,
  actions,
  refreshToken,
  sortValueOf,
}: Props<T>) {
  const { enqueueSnackbar } = useSnackbar();
  const currentUser = useCurrentUser();
  const [readFailed, setReadFailed] = useState(false);

  const data = useCallback(
    async (params: { [key: string]: any }) => {
      const page = Number(params.page ?? 1);
      const size = Number(params.size ?? 15);
      try {
        const loaded = await load();
        const all = sortValueOf && params.sort ? sortRows(loaded, params.sort, sortValueOf) : loaded;
        setReadFailed(false);
        return { total: all.length, rows: all.slice((page - 1) * size, page * size) };
      } catch {
        setReadFailed(true);
        enqueueSnackbar(`Error fetching ${what}`, { variant: "error" });
        return { total: 0, rows: [] };
      }
    },
    // refreshToken is not read: it changes this callback's identity, which is
    // what makes the grid fetch again after a write (BP-80).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [load, what, enqueueSnackbar, refreshToken, sortValueOf]
  );

  return (
    <DetailPremiumBox data-testid={testId}>
      <AeronetDataGridPremium<T>
        autoHeight
        name={name}
        currentUserId={currentUser?.id as number}
        data={data}
        columns={columns}
        enableCreate={!!onCreate}
        onCreate={onCreate}
        actions={actions}
        checkboxSelection={!!actions?.length}
        noRowsLabel={readFailed ? `${what} unavailable — the read failed.` : noRowsLabel}
        getRowClassName={getRowClassName}
        initialState={{ density: "compact" }}
        sx={sx}
      />
    </DetailPremiumBox>
  );
}
